import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from "styled-components";
import { Journey, Leg, TaggedJourney, TransferStat, JourneyWithHafas } from "../../utils/mapJourneysData";
import { fetchJourneys } from '../../utils/fetchJourneys';
import { colors } from '../../utils/colors';
import { slateColors, purpleColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import * as AlternativeButtons from '../Controls/AlternativeButtons';
import JourneyLegCard from './JourneyLegCard';
import Transfer from "./Transfer";
import KPIsContainer from "./KPIsContainer";
import PageHeader from '../PageHeader';
import FeedbackCard from '../Elements/FeedbackCard';
import AppLinkCard from '../Elements/AppLinkCard';
import { getApi } from '../../utils/api';
import mixpanel from 'mixpanel-browser';
import { decodeHafasReference, encodeHafasReference, HafasLeg, HafasReference, stripRedundantAttributes } from '../../utils/hafas-reference';
import { compressToURL } from '../../utils/compress';


const Container = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  box-sizing: border-box;
  margin-top: 24px;
  margin-bottom: 48px;
  gap: 32px;
  @media (min-width: 768px) {
    padding: 0px 64px;  // Increased horizontal padding for desktop
  }
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem;
  background: white;
  border-bottom: 2px solid ${slateColors[200]};
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    gap: 0.75rem;
  }
`;

const LegsContainer = styled('div')`
  @media (min-width: 768px) {
    max-width: 600px;
  }
`

const TransferContainer = styled('div')`
  padding: 16px 16px 16px 24px;
  margin-left: 24px;
  position: relative;
  display: flex;
  grid-row-gap: 14px;
  flex-direction: column;
  &:before {
    content: "";
    position: absolute;
    top: -20px;
    left: 2px;
    bottom: -24px;
    width: 2px;
    background-color: ${colors.grey600};
    z-index: -1;
  }
`

const AlternativesContainer = styled('div')`
  display: inline-flex;
  align-items: center;
  align-content: center;
  gap: 12px;
  flex-wrap: wrap;
`


type JourneyDetailsScreenProps = {
  journey: JourneyWithHafas;
}

// Define the fetchLaterDepartures function
const fetchLaterDepartures = async (
  originId: string, 
  destinationId: string, 
  plannedDeparture: string, 
  departure: string,
  onJourneysFetched: (journeys: TaggedJourney[], newEarlierThan: string | null, newLaterThan: string | null, newDepartureTime: string) => void, // Pass newDepartureTime here
  onError: (message: string) => void,
  onLoadingChange: (isLoading: boolean) => void
) => {
  // Determine the correct departure time
  const departureTimeStr = departure || plannedDeparture; // Use `departure` if available, otherwise use `plannedDeparture`

  // Parse the departureTime and add 1 minute
  const departureTime = new Date(departureTimeStr);
  departureTime.setMinutes(departureTime.getMinutes() + 1);

  // Format the updated departureTime as an ISO string (you can format it based on the API requirements)
  const newDepartureTime = departureTime.toISOString();

  // Call fetchJourneys with the correct parameters
  fetchJourneys({
    type: 'initial',
    originId: originId,
    destinationId: destinationId,
    date: newDepartureTime,
    tripType: 'departure', 
    options: {}, // If you have options, you can pass them here
    earlierThan: null, // No earlier journeys needed
    laterThan: null,  // No later journeys needed
    onJourneysFetched: (journeys, newEarlierThan, newLaterThan) => onJourneysFetched(journeys, newEarlierThan, newLaterThan, newDepartureTime),  // Pass the newDepartureTime to onJourneysFetched
    onError: onError,  // Callback for error handling
    onLoadingChange: onLoadingChange,  // Callback to show/hide loading indicator
  });
}

// Define the fetchLaterDepartures function
const fetchEarlierArrivals = async (
  originId: string, 
  destinationId: string, 
  plannedArrival: string, 
  arrival: string,
  onJourneysFetched: (journeys: any[], newEarlierThan: string | null, newLaterThan: string | null, newArrivalTime: string) => void, // Pass newArrivalTime here
  onError: (message: string) => void,
  onLoadingChange: (isLoading: boolean) => void
) => {
  // Determine the correct departure time
  const arrivalTimeStr = arrival || plannedArrival; // Use `arrival` if available, otherwise use `plannedArrival`

  // Parse the departureTime and add 1 minute
  const arrivalTime = new Date(arrivalTimeStr);
  arrivalTime.setMinutes(arrivalTime.getMinutes() - 1);

  // Format the updated departureTime as an ISO string (you can format it based on the API requirements)
  const newArrivalTime = arrivalTime.toISOString();

  // Call fetchJourneys with the correct parameters
  fetchJourneys({
    type: 'initial',
    originId: originId,
    destinationId: destinationId,
    date: newArrivalTime,
    tripType: 'arrival', 
    options: {}, // If you have options, you can pass them here
    earlierThan: null, // No earlier journeys needed
    laterThan: null,  // No later journeys needed
    onJourneysFetched: (journeys, newEarlierThan, newLaterThan) => onJourneysFetched(journeys, newEarlierThan, newLaterThan, newArrivalTime),  // Pass the newArrivalTime to onJourneysFetched
    onError: onError,  // Callback for error handling
    onLoadingChange: onLoadingChange,  // Callback to show/hide loading indicator
  });
}

async function fetchTransferStats(firstLegLineName: string, firstLegDestinationId: string, secondLegLineName: string, secondLegOriginId: string): Promise<TransferStat | undefined> {
  try {
    const requestBody = {
      firstLegLineName,
      firstLegDestinationId,
      secondLegLineName,
      secondLegOriginId,
    };

    return new Promise((resolve, reject) => {
      getApi('transferstats', requestBody)
        .then(data => resolve(data))
        .catch(error => {
          console.error('Fetch error:', error);
          reject(error);
        });
    });
  } catch (error) {
    console.error('Error fetching transfer stats:', error);
    return undefined;
  }
}

const JourneyDetailsScreen = ({ journey }: JourneyDetailsScreenProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false); // State for button loading
  const [transferLoadingIndex, setTransferLoadingIndex] = useState<number | null>(null); // To track which transfer is loading
  const _originalState = {legs: journey.legs, hafas: journey.hafas};
  const [state, setState] = useState(_originalState);
  const [originalJourney] = useState(journey);
  const [originalState] = useState(_originalState);
  const [isShowingAlternative, setIsShowingAlternative] = useState(false);
  const [currentSearchType, setCurrentSearchType] = useState<"departure" | "arrival" | null>(null);

  // Determine if journey was accessed from within the app or via shared URL
  const fromPath = (location.state?.from as string) || '';
  const inApp = Boolean(fromPath);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top of the page when the component is mounted
  }, []);
  
  const origin = state.legs[0].origin.name;
  const destination = state.legs[state.legs.length - 1].destination.name;
  const departure = new Intl.DateTimeFormat('en-GB', {
    weekday: "long",
    month: "numeric",
    day: "numeric",
  }).format(new Date(state.legs[0].departure!))

  const fetchTransferStatsAndUpdateLegs = async (
    firstLeg: Leg,
    secondLeg: Leg,
    transferUpdateIndex: number,
    searchType: "departure" | "arrival"
  ) => {
    try {
      setTransferLoadingIndex(transferUpdateIndex); // Start loading
  
      const transferStats = await fetchTransferStats(
        firstLeg.line.name,
        firstLeg.destination.id,
        secondLeg.line.name,
        secondLeg.origin.id
      );
  
      if (transferStats) {
        setState(current => ({
          legs: current.legs.map((leg, idx) => {
            if (idx === transferUpdateIndex) {
              return {
                ...leg,
                transferStats
              };
            } else {
              return leg;
            }
          }),
          hafas: current.hafas,
        }));
      }
    } catch (error) {
      console.error('Error fetching transfer stats:', error);
      setState(current => ({
        legs: current.legs.map((leg, idx) => {
          if (idx === transferUpdateIndex) {
            const { transferStats: _, ...legWithoutStats } = leg as any;
            return legWithoutStats;
          } else {
            return leg;
          }
        }),
        hafas: current.hafas
      }));
    } finally {
      setTransferLoadingIndex(null); // End loading
    }
  };

  // Define onJourneysFetched
  const onJourneysFetched = (
    journeys: TaggedJourney[],
    newTime: string, // newDepartureTime or newArrivalTime
    searchType: "departure" | "arrival" // Determine search type
  ) => {
    // Filter journeys based on the first leg's departure time
    const filteredJourneys = journeys.filter(journey => {
      const firstLeg = journey.legs[0];
      const lastLeg = journey.legs[journey.legs.length - 1];

      if (searchType === "departure") {
        const firstLegDeparture = firstLeg.departure || firstLeg.plannedDeparture;
        return new Date(firstLegDeparture) >= new Date(newTime); // Filter later departures
      } else if (searchType === "arrival") {
        const lastLegArrival = lastLeg.arrival || lastLeg.plannedArrival;
        return new Date(lastLegArrival) <= new Date(newTime); // Filter earlier arrivals
      }
      return false;
    });

    if (filteredJourneys.length > 0) {
      // Determine whether to use the first or last journey based on searchType
      const newJourney = searchType === "departure"
        ? filteredJourneys[0] // Use the first journey for later departure
        : filteredJourneys[filteredJourneys.length - 1]; // Use the last journey for earlier arrival

      const newLegs = newJourney.legs;

      setState(current => {
        
        // Find the search point index in the original legs
        const searchPointIndex = searchType === "departure"
          ? current.legs.findIndex(leg => leg.origin.id === newLegs[0].origin.id) // Compare origins for later departures
          : current.legs.findIndex(leg => leg.destination.id === newLegs[newLegs.length - 1].destination.id); // Compare destinations for earlier arrivals
      
        // If the search point is found, combine legs
        if (searchPointIndex !== -1) {
          // Create the combined journey
          const combinedLegs = searchType === "departure"
            ? [
                ...current.legs.slice(0, searchPointIndex), // Original legs up to the search point
                ...newLegs // New legs from the alternative journey (later departure)
              ]
            : [
                ...newLegs, // New legs from the alternative journey (earlier arrival)
                ...current.legs.slice(searchPointIndex + 1) // Original legs after the search point
              ];

          setIsShowingAlternative(true);

          // For arrival searches, adjust the index to update the correct leg
          const transferUpdateIndex = searchType === "arrival" ? newLegs.length - 1 : searchPointIndex - 1;

          // Fetch transfer stats after updating legs
          const firstLeg = searchType === "departure" ? current.legs[searchPointIndex - 1] : newLegs[newLegs.length - 1];
          const secondLeg = searchType === "departure" ? newLegs[0] : current.legs[searchPointIndex + 1];

          if (firstLeg && secondLeg) {
            fetchTransferStatsAndUpdateLegs(firstLeg, secondLeg, transferUpdateIndex, searchType);
          }

          // do the same for hafas token!
          const currentHafas = current.hafas.legs;
          const newHafas = newJourney.hafas.legs;

          const searchPointIndexHafas = searchType === "departure"
            ? currentHafas.findIndex(leg => leg.from.L === newHafas[0].from.L)
            : currentHafas.findIndex(leg => leg.to.L === newHafas[newHafas.length - 1].to.L);

          if (searchPointIndexHafas !== -1) {
            const combinedHafasLegs = searchType === "departure"
              ? [...currentHafas.slice(0, searchPointIndexHafas), ...newHafas]
              : [...newHafas, ...currentHafas.slice(searchPointIndexHafas + 1)];

            const newHafasRef: HafasReference = {
              ...newJourney.hafas,
              legs: combinedHafasLegs,
            };

            const newId = compressToURL(encodeHafasReference(stripRedundantAttributes(newHafasRef)));
            const updatedJourney: JourneyWithHafas = {
              ...journey,
              id: newId,
              legs: combinedLegs
            };
            navigate(`/journey/${newId}`, { replace: true, state: { journey: updatedJourney } });
            return { legs: combinedLegs, hafas: newHafasRef };
          }

          // may never be relevant as the navigate call above supersedes it
          return { legs: combinedLegs, hafas: current.hafas };
        }
  
        return current; // Return original state if no search point is found
      });
    }
  };

  // Define onError
  const onError = (message: string) => {
    console.error("Error fetching journeys:", message);
  };

  // Define onLoadingChange
  const onLoadingChange = (isLoading: boolean) => {
    setIsLoading(isLoading); // Toggle the loading state
  };

  const handleSearchClick = async (index: number, searchType: "departure" | "arrival") => {
    const nextLeg = trainLegs[index + 1];
    const prevLeg = trainLegs[index]; // Previous leg for earlier arrival
    
    try {
      if (searchType === "departure" && nextLeg) {
        const originId = nextLeg.origin.id;
        const destinationId = trainLegs[trainLegs.length - 1].destination.id;
        const plannedDeparture = nextLeg.plannedDeparture;
        const departure = nextLeg.departure;

        mixpanel.track('Depart Later Button Tapped', {
          from_station_id: originId,
          to_station_id: destinationId,
        });
        setCurrentSearchType("departure");
    
        await fetchLaterDepartures( // Await if needed here
          originId,
          destinationId,
          plannedDeparture,
          departure!,
          (journeys, newEarlierThan, newLaterThan, newDepartureTime) => 
            onJourneysFetched(journeys, newDepartureTime, "departure"),
          onError,
          onLoadingChange
        );
        
      } else if (searchType === "arrival" && prevLeg) {
        const originId = trainLegs[0].origin.id; // Origin from first leg
        const destinationId = prevLeg.destination.id;
        const plannedArrival = prevLeg.plannedArrival;
        const arrival = prevLeg.arrival;

        mixpanel.track('Arrive Earlier Button Tapped', {
          from_station_id: originId,
          to_station_id: destinationId,
        });
        setCurrentSearchType("arrival");
    
        await fetchEarlierArrivals(
          originId,
          destinationId,
          plannedArrival,
          arrival!,
          (journeys, newEarlierThan, newLaterThan, newArrivalTime) => 
            onJourneysFetched(journeys, newArrivalTime, "arrival"),
          onError,
          onLoadingChange
        );
      }
    } catch (error) {
      console.error("Error in search click handling:", error);
    }
  };

  const handleToggleJourney = () => {
    mixpanel.track('Revert To Original Leg Button Tapped', {
      modification_type: isShowingAlternative ? (currentSearchType === "departure" ? "depart_later" : "arrive_earlier") : undefined,
    });
    
    if (isShowingAlternative) {
      // Navigate back to original journey
      navigate(`/journey/${originalJourney.id}`, { replace: true, state: { journey: originalJourney } });
    }
    
    setState(isShowingAlternative ? originalState : state);
    setIsShowingAlternative(!isShowingAlternative);
  };

  // Filter out walking legs
  const trainLegs = state.legs.filter(leg => !leg.walking);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top of the page
  }, []);

  return (
    <>
      <Helmet>
        <title>{`${origin} → ${destination} | Journey Details | chuuchuu`}</title>
        <meta name="description" content={`Journey details for ${origin} to ${destination} on ${departure}. View delay predictions and transfer reliability.`} />
      </Helmet>
      <HeaderContainer>
        <PageHeader
          breadcrumbs={[
            { label: t('common.home'), path: "/" },
            ...(inApp ? [{ label: t('pageHeader.journeys'), path: "/journeys" }] : []),
            { 
              label: t('journeyDetails.title'), 
              path: journey?.id ? `/journey/${journey.id}` : undefined, 
              active: true 
            }
          ]}
          title={`${origin} ➜ ${destination}`}
          subtitle={`${departure}`}
        />
      </HeaderContainer>
      <Container>
        <KPIsContainer journey={journey} />
        <LegsContainer>
          {trainLegs.map((leg, index) => (
            <React.Fragment key={index}>
              <JourneyLegCard leg={leg} />
                {index < trainLegs.length - 1 ? (
                  <>
                    <TransferContainer>
                      <AlternativesContainer>
                        <AlternativeButtons.EarlierArrivalButton
                          onClick={() => handleSearchClick(index, "arrival")}
                          isLoading={isLoading} 
                        />
                        {isShowingAlternative && !isLoading && (
                          <AlternativeButtons.OriginalJourneyButton 
                            onClick={handleToggleJourney} // Toggle back to the original journey
                          />
                        )}
                      </AlternativesContainer>

                      <Transfer 
                        index={index} 
                        journey={{ ...journey, legs: trainLegs }} 
                        isLoading={transferLoadingIndex === index}  // Pass the loading state
                      />     
                                    
                      <AlternativesContainer>
                        <AlternativeButtons.LaterDepartureButton
                          onClick={() => handleSearchClick(index, "departure")}
                          isLoading={isLoading} 
                        />
                        {isShowingAlternative && !isLoading && (
                          <AlternativeButtons.OriginalJourneyButton 
                            onClick={handleToggleJourney} // Toggle back to the original journey
                          />
                        )}
                      </AlternativesContainer>
                    </TransferContainer>
                  </>
                ) : null}
            </React.Fragment>
          ))}
        </LegsContainer>
        <AppLinkCard />
        <FeedbackCard 
          source={`${t('journeyDetails.feedbackSource')} ${origin} to ${destination} ${t('journeyDetails.on')} ${departure}`}
          journey={journey}
        />
      </Container>
    </>
  )
}

export default JourneyDetailsScreen;
